import type { BreakdownAnalysis } from '~/components/Forms/purchase/breakdown.vue'
import useNotificationApi from '~/services/apis/notifications'
import { usePurchaseApi } from '~/services/apis/purchase'
import type { PaymentResponse } from '~/components/Gateway/channel-modal.vue'
import type { ProductFormDataContext, RecordSchema } from '~/types'
import type { ListContext } from '@/components/Shared/list-item.vue'

type ProductRequestContext = {
	product: ProductsResponse | null
	loadingProduct: boolean
	productId: string
}
const PRODUCT_REQUEST_KEY = 'product-request-key'

export const provideProductContext = (value: MaybeRefOrGetter<ProductRequestContext>) => {
	const item = toRef(value)
	provide(PRODUCT_REQUEST_KEY, item)
	return value
}

export const useProductContext = () => {
	return inject<Ref<ProductRequestContext | null>>(
		PRODUCT_REQUEST_KEY,
		ref({
			product: null,
			loadingProduct: false,
			productId: ''
		})
	)
}

export const useProductDetails = (productInfo?: MaybeRefOrGetter<ProductsResponse | null>) => {
	const { productCategoryType, toLower, userSource } = useUtils()
	const { purchasePlanEndpoints } = usePaystack()
	const { User } = reactor(useUser())
	const { hasAnyPermission } = reactor(usePermissionStore())

	const { getTodoList } = useNotificationApi()
	const mixpanel = useMixpanel()
	const addedPlanId = shallowRef('')
	const sumNote = shallowRef()
	const isSubmitting = shallowRef(false)

	const { currencyToNaira } = useCurrency()
	// This is only available we want to use this composable in a child component
	const contextResponse = useProductContext()
	const productPayload = toRef(productInfo || null)
	const productDetails = computed(() => productPayload.value || contextResponse.value?.product || null)
	const productCategory = computed(() => toLower(productDetails.value?.productCategory?.name ?? ''))
	const planName = computed(() => {
		return productDetails.value?.name ?? 'N/A'
	})
	const providerName = computed(() => toLower(productDetails.value?.underwriter ?? ''))
	const isHygeia = computed(() => providerName.value === Providers.hygeia)
	const isBastion = computed(() => providerName.value === Providers.bastion)
	const productCode = computed(() => Number(productDetails.value?.code_name ?? 0))
	const categoryType = computed(() => productCategoryType(productCategory.value || '', productCode.value))
	const formName = computed(() => productDetails.value?.form_name ?? '')
	const price = computed(() => Number(productDetails.value?.price?.market_price ?? 0))
	const planFirstName = computed(() => (planName.value && planName.value.split(' ')[0]) || '')
	const policyLabel = computed(() => {
		return categoryType.value?.isAuto
			? 'vehicle'
			: productCategory.value !== 'other'
				? productCategory.value
				: planFirstName
	})
	const purchaseTemplate: Record<string, string> = {
		health: 'forms-purchase-health',
		travel: 'forms-purchase-travel',
		gadget: 'forms-purchase-gadget',
		auto: 'forms-purchase-auto',
		comprehensiveAuto: 'forms-purchase-auto-comprehensive'
	}
	const currentTemplate = computed(() => {
		if (categoryType.value?.isComprehensive) {
			return purchaseTemplate.comprehensiveAuto
		}
		return purchaseTemplate[productCategory.value]
	})
	const productCaptionTag = computed(() => {
		let label = categoryType.value?.isAuto ? '/vehicle/annually' : ''
		let formattedPrice = currencyToNaira(price.value)
		if (categoryType.value?.isComprehensive || categoryType.value?.isGadget) {
			formattedPrice = `Starts from ${
				productDetails.value?.price?.is_dynamic_price ? `${price.value}%` : formattedPrice
			} of ${productDetails.value?.name} value`
			label = categoryType.value?.isGadget ? '/gadget yearly' : ' /vehicle/annually'
		}
		if (categoryType.value?.isLaptopCover) {
			formattedPrice = ` ${
				productDetails.value?.price?.is_dynamic_price ? `${price.value}%` : formattedPrice
			} of laptop value`
			label = '/per annum'
		}
		if (categoryType.value.isHealth) {
			label = '/person/bi-annual'
		}
		if (categoryType.value?.isTravel) {
			formattedPrice = 'Premium based on'
			label = 'Duration of travel, Country, Age'
		}

		return {
			title: formattedPrice,
			label
		}
	})
	const productProvider = computed(() => productDetails.value?.underwriter ?? '')
	const breakdownAnalysis = computed<BreakdownAnalysis>(() => {
		return {
			label: '',
			intro: {
				done: !!sumNote.value,
				value: productCaptionTag.value.title,
				label: productCaptionTag.value.label ?? '/vehicle annually'
			},
			value: sumNote.value?.totalAmount ?? 0,
			discount_name: sumNote.value?.discount_name ?? '',
			original_price: sumNote.value?.original_price ?? 0,
			expandable: sumNote.value?.expandable ?? false
		}
	})
	const isFormDataAvailable = computed(() => {
		return !!Object.values(formData.value || {}).length
	})
	const groupId = computed(() => productDetails.value?.group_id)

	// Mixpanel Event
	const paymentEventName = computed(() => `buy_${planName.value}`)
	const paymentEventCategory = computed(() => `${productCategory.value}`)
	const familyProps = {
		min: FAMILYPLANMIN,
		max: FAMILYPLANMAX
	}

	// Forms
	const formData = ref<ProductFormDataContext<AutoPurchaseFormContext | AutoPurchaseResponse>['formData']>()

	function handleBeforeUnload(e: Event) {
		// Optional: Prompt the user to confirm leaving the page
		e.preventDefault()
	}

	const { purchaseAutoVehicles, purchaseGadget, purchaseHealth, purchaseTavel, purchaseComprehensiveAuto } =
		usePurchaseApi()
	type FormDataContexts =
		| AutoPurchaseFormContext
		| ComprehensivePurchaseContext
		| GadgetPurchaseContext
		| HealthPurchaseContext
		| TravelPurchaseContext
	type RequestResponse = AutoPurchaseResponse | ComputeGadgetResponse | HealthPurchaseResponse | ComputeTravelResponse
	const mixpanelProps = computed(() => ({
		plan_name: planName.value,
		premium: formData.value?.price ?? 0,
		product_category: productCategory.value,
		cover_period: formData.value?.cover_period ?? ''
	}))
	const submitPlan = async (arg: PaymentResponse, nextStep: Function) => {
		if (!hasAnyPermission.value([PERMISSIONS.PLAN.PURCHASE]) && User.value?.role !== 'admin') {
			return useNotification().alert({
				status: 400,
				message: 'This account is not authorized to purchase a plan'
			})
		}
		resolve(() => mixpanel?.track('purchase_biz_clicked', mixpanelProps.value))

		return resolve(async () => {
			const response = {
				...formData.value,
				...(userSource.value && { channel: userSource.value })
			} as FormDataContexts
			const payment_channel = arg.channel
			const requestResponse = ref<IRequestResponse<RequestResponse> | null>(null)
			const transactionId = arg.transaction

			if (categoryType.value?.isComprehensive) {
				requestResponse.value = await purchaseComprehensiveAuto({
					...(response as ComprehensivePurchaseContext),
					payment_channel,
					transaction_id: transactionId
				})
			} else if (categoryType.value?.isAuto) {
				requestResponse.value = await purchaseAutoVehicles(formName.value as '', {
					...(response as AutoPurchaseFormContext),
					payment_channel,
					transaction_id: transactionId
				})
			} else if (categoryType.value?.isGadget) {
				requestResponse.value = await purchaseGadget({
					...(response as GadgetPurchaseContext),
					payment_channel,
					transaction_id: transactionId
				})
			} else if (categoryType.value.isHealth) {
				requestResponse.value = await purchaseHealth({
					...(response as HealthPurchaseContext),
					payment_channel,
					transaction_id: transactionId
				})
			} else if (categoryType.value?.isTravel) {
				requestResponse.value = await purchaseTavel({
					...(response as TravelPurchaseContext),
					payment_channel,
					transaction_id: transactionId
				})
			}
			if (requestResponse.value?.data) {
				resolve(() => {
					useGtag()?.gtag('event', 'payment_successful', mixpanelProps.value)
					mixpanel?.track('purchase_biz_successful', mixpanelProps.value)
				})
				addedPlanId.value = requestResponse.value.data.id || ''
				window.removeEventListener('beforeunload', handleBeforeUnload)
				nextStep('product-complete')
				useNotification().alert({
					status: 200,
					message: requestResponse.value.responseText
				})
				// Get the todo List
				await getTodoList()
				// Clear campaign codes and source if any
				localStorage.removeItem('campaign_code')
				localStorage.removeItem('channel_source')
			}
		}, isSubmitting)
	}
	const getPlanBreakdown = (param: unknown) => {
		// use 'isEmpty' key  to know when to display the breakdown summary
		sumNote.value = param
	}
	const formSummary = shallowRef<ListContext | null>(null)
	const getFormData = (param: ProductFormDataContext<RecordSchema>, nextStep: Function) => {
		formData.value = param.formData as unknown as typeof formData.value
		formSummary.value = param.summary as ListContext
		nextStep('product-summary')
	}

	return {
		familyProps,
		providerName,
		isHygeia,
		isBastion,
		groupId,
		productDetails,
		productCategory,
		planName,
		productCode,
		categoryType,
		formName,
		price,
		planFirstName,
		policyLabel,
		currentTemplate,
		productCaptionTag,
		productProvider,
		paymentEventName,
		paymentEventCategory,
		endpoint: computed(() => purchasePlanEndpoints(productCategory.value, productCode.value)),
		formData,
		addedPlanId,
		isSubmitting,
		breakdownAnalysis,
		isFormDataAvailable,
		User,
		formSummary,
		sumNote,
		mixpanelProps,
		submitPlan,
		getPlanBreakdown,
		getFormData
	}
}
